import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import reviewers from '../assets/reviewer_committee_list.json';
import data from '../assets/editorialBoard.json';
import profiles from '../assets/editorProfiles.json';
import '../CSS/EditorialBoard.css';

// --- Utilities ---
// Preserve the input JSON order for both roles and members (no alphabetic sort)
const groupByRole = (items) => {
    const map = new Map();
    const roleOrder = [];
    items.forEach(m => {
        const role = m.role || 'Member';
        if (!map.has(role)) { map.set(role, []); roleOrder.push(role); }
        map.get(role).push(m); // keep insertion order
    });
    return roleOrder.map(r => ({ title: r, members: map.get(r) }));
};

const avatarColors = [
    'hsl(210,50%,18%)',
    'hsl(200,70%,45%)',
    'hsl(205,55%,32%)',
    'hsl(198,65%,52%)',
    'hsl(210,40%,28%)',
    'hsl(195,60%,40%)'
];

let imageContext;
try { imageContext = require.context('../Images', false, /\.(png|jpe?g|webp|svg)$/); } catch { imageContext = null; }
const getImage = (filename) => {
    if (!filename || !imageContext) return null;
    try { return imageContext('./' + filename); } catch { return null; }
};
const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (!parts.length) return '?';
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};
const colorFor = (name) => avatarColors[name.charCodeAt(0) % avatarColors.length];

// Normalize salutation across possible JSON key variants
const getSalutation = (r) => {
    const val = (r && (r['Salutation\u00A0'] || r['Salutation '] || r['Salutation'])) || '';
    return (val || 'Dr.').toString().trim();
};

// Inline icons (no external deps)
const MailIcon = (props) => (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
    </svg>
);
const MapPinIcon = (props) => (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 1 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
    </svg>
);

const displayRole = (role) => role || 'Member';
const sectionTitle = (title) => title || 'Section';

// Icons for profile modal
const AcademicCapIcon = () => (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
    </svg>
);
const BookOpenIcon = () => (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
    </svg>
);
const AwardIcon = () => (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="7"/><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/>
    </svg>
);
const LinkIcon = () => (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
    </svg>
);
const CloseIcon = () => (
    <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
);

// Modal Component
const EditorProfileModal = ({ member, profile, onClose }) => {
    if (!profile) return null;
    const img = getImage(member.image);
    const initials = getInitials(member.name);
    const bg = colorFor(member.name || '?');

    return (
        <div className="eb-modal-overlay" onClick={onClose}>
            <div className="eb-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="eb-modal-close" onClick={onClose} aria-label="Close modal">
                    <CloseIcon />
                </button>

                <div className="eb-modal-header">
                    <div className="eb-modal-photo" style={!img ? { background: bg } : undefined}>
                        {img ? <img src={img} alt={member.name} /> : <div className="eb-initial-avatar" style={{fontSize: '2rem'}}>{initials}</div>}
                    </div>
                    <div className="eb-modal-header-text">
                        <h2 className="eb-modal-name">{member.name}</h2>
                        <div className="eb-modal-role">{displayRole(member.role)}</div>
                        {member.affiliation && <p className="eb-modal-affiliation">{member.affiliation}</p>}
                    </div>
                </div>

                <div className="eb-modal-body">
                    {profile.bio && (
                        <section className="eb-modal-section">
                            <h3 className="eb-modal-section-title">Biography</h3>
                            <p className="eb-modal-text">{profile.bio}</p>
                        </section>
                    )}

                    {profile.institution_details && (
                        <section className="eb-modal-section">
                            <h3 className="eb-modal-section-title"><MapPinIcon style={{width:18,height:18,marginRight:8,verticalAlign:'middle'}}/>Institution Details</h3>
                            <div className="eb-modal-institution">
                                <p><strong>Institution:</strong> {profile.institution_details.name}</p>
                                {profile.institution_details.department && <p><strong>Department:</strong> {profile.institution_details.department}</p>}
                                {profile.institution_details.location && <p><strong>Location:</strong> {profile.institution_details.location}</p>}
                                {profile.institution_details.website && (
                                    <p><strong>Website:</strong> <a href={profile.institution_details.website} target="_blank" rel="noopener noreferrer">{profile.institution_details.website}</a></p>
                                )}
                            </div>
                        </section>
                    )}

                    {profile.education && profile.education.length > 0 && (
                        <section className="eb-modal-section">
                            <h3 className="eb-modal-section-title"><AcademicCapIcon/> Education</h3>
                            <ul className="eb-modal-list">
                                {profile.education.map((edu, i) => <li key={i}>{edu}</li>)}
                            </ul>
                        </section>
                    )}

                    {profile.research_interests && profile.research_interests.length > 0 && (
                        <section className="eb-modal-section">
                            <h3 className="eb-modal-section-title"><BookOpenIcon/> Research Interests</h3>
                            <div className="eb-modal-tags">
                                {profile.research_interests.map((interest, i) => (
                                    <span key={i} className="eb-tag">{interest}</span>
                                ))}
                            </div>
                        </section>
                    )}

                    {profile.achievements && profile.achievements.length > 0 && (
                        <section className="eb-modal-section">
                            <h3 className="eb-modal-section-title"><AwardIcon/> Key Achievements</h3>
                            <ul className="eb-modal-list">
                                {profile.achievements.map((ach, i) => <li key={i}>{ach}</li>)}
                            </ul>
                        </section>
                    )}

                    {(profile.google_scholar || profile.orcid || profile.linkedin || (profile.publications_count > 0)) && (
                        <section className="eb-modal-section">
                            <h3 className="eb-modal-section-title"><LinkIcon/> Academic Profiles & Metrics</h3>
                            <div className="eb-modal-links">
                                {profile.google_scholar && (
                                    <a href={profile.google_scholar} target="_blank" rel="noopener noreferrer" className="eb-profile-link">
                                        Google Scholar
                                    </a>
                                )}
                                {profile.orcid && (
                                    <a href={profile.orcid} target="_blank" rel="noopener noreferrer" className="eb-profile-link">
                                        ORCID
                                    </a>
                                )}
                                {profile.linkedin && (
                                    <a href={profile.linkedin} target="_blank" rel="noopener noreferrer" className="eb-profile-link">
                                        LinkedIn
                                    </a>
                                )}
                            </div>
                            {(profile.publications_count > 0 || profile.h_index > 0) && (
                                <div className="eb-metrics">
                                    {profile.publications_count > 0 && <div className="eb-metric"><span className="eb-metric-value">{profile.publications_count}</span> Publications</div>}
                                    {profile.h_index > 0 && <div className="eb-metric"><span className="eb-metric-value">{profile.h_index}</span> h-index</div>}
                                </div>
                            )}
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
};

const MemberCard = ({ member, onViewMore }) => {
    const img = getImage(member.image);
    const initials = getInitials(member.name);
    const bg = colorFor(member.name || '?');
    const hasProfile = profiles[member.id];
    const hasInstProfile = !!member.institutionalProfileUrl;

    return (
        <div className="eb-card modern">
            <div className="eb-card-header">
                <div className="eb-photo-frame avatar" style={!img ? { background: bg } : undefined}>
                    {img ? <img src={img} alt={member.name} /> : <div className="eb-initial-avatar big" aria-label={member.name}>{initials}</div>}
                </div>
                <div className="eb-role-badge">{displayRole(member.role)}</div>
            </div>
            
            <div className="eb-card-body">
                <h3 className="eb-name-lg">{member.name}</h3>
                <div className="eb-info-list">
                    {member.affiliation && (
                        <div className="eb-info-line">
                            <MapPinIcon className="eb-icon" />
                            <span className="eb-affiliation-lg">{member.affiliation}</span>
                        </div>
                    )}
                    {member.email && (
                        <div className="eb-info-line">
                            <MailIcon className="eb-icon" />
                            <a href={`mailto:${member.email}`} className="eb-email-link" style={{ cursor: 'pointer' }}>
                                {member.email}
                            </a>
                        </div>
                    )}
                </div>
            </div>

            <div className="eb-card-footer">
                <div className="eb-card-actions">
                    {hasProfile && (
                        <button className="eb-view-more-btn" onClick={() => onViewMore(member)}>
                            View More →
                        </button>
                    )}
                    {hasInstProfile && (
                        <a
                            href={member.institutionalProfileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="eb-inst-btn"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                            Institutional Profile
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};

const EditorialBoard = () => {
    const sections = useMemo(() => groupByRole(data), []);
    const [selectedMember, setSelectedMember] = useState(null);

    const handleViewMore = (member) => {
        setSelectedMember(member);
    };

    const handleCloseModal = () => {
        setSelectedMember(null);
    };

    return (
            <div className="eb-modern-wrapper">
                <header className="eb-clean-header">
                    <h1 className="eb-clean-title">ITME Editorial Leadership</h1>
                    <p className="eb-clean-sub">A collaborative panel of editors and subject experts supporting quality, integrity, and multidisciplinary relevance.</p>
                </header>
            {sections.map((section, idx) => (
                <section key={section.title} className="eb-modern-section" style={idx>0?{marginTop: '40px'}:undefined}>
                    <h2>{sectionTitle(section.title)}</h2>
                    <div className="eb-grid-modern">
                        {section.members.map(m => <MemberCard key={m.email || m.id || m.name} member={m} onViewMore={handleViewMore} />)}
                    </div>
                </section>
            ))}
            {/* Reviewer Committee preview section */}
            <section className="eb-modern-section" style={{marginTop: '52px'}}>
                <h2>Reviewer Committee</h2>
                <div className="eb-grid-modern">
                    {reviewers.slice(0,6).map((r) => (
                        <div key={r.ID} className="eb-card modern" style={{gap:14}}>
                            <div style={{width:56,height:56,borderRadius:14,background:'linear-gradient(135deg,#0b5d73,#13a1b4)',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:700,flexShrink:0}}>
                                {(r['Full Name']||'?').toString().trim().split(/\s+/).slice(0,2).map(w=>w[0]).join('').toUpperCase()}
                            </div>
                            <div className="eb-card-body">
                                <div className="eb-role-badge">{getSalutation(r)}</div>
                                <h3 className="eb-name-lg" style={{marginBottom:2}}>{(r['Full Name']||'').toString().trim()}</h3>
                                {r['Current Designation'] && (
                                    <div className="eb-affiliation-lg" style={{marginBottom:4}}>{r['Current Designation']}</div>
                                )}
                                {r['Institutional Affiliation'] && (
                                    <div className="eb-info-line"><span className="eb-affiliation-lg" style={{margin:0}}>{r['Institutional Affiliation']}</span></div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:16}}>
                    <div style={{color:'#32535c',fontSize:14}}>Peer review support: <strong>{reviewers.length}</strong> reviewers</div>
                    <Link to="/reviewers" style={{color:'#0a6079',fontWeight:600}}>View all reviewers →</Link>
                </div>
            </section>
            {selectedMember && (
                <EditorProfileModal
                    member={selectedMember}
                    profile={profiles[selectedMember.id]}
                    onClose={handleCloseModal}
                />
            )}
        </div>
    );
};

export default EditorialBoard;
